import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Send, FlaskConical, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const CampaignDialog = ({ open, onOpenChange, recipients, onSent }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [raffleId, setRaffleId] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [raffles, setRaffles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/raffles").then((r) => setRaffles(r.data)).catch(() => setRaffles([]));
  }, [open]);

  const payload = () => ({
    subject: subject.trim(),
    body_html: body,
    link: link.trim(),
    raffle_id: raffleId || null,
    image_url: imageUrl,
    customer_ids: recipients.map((c) => c.id),
    test_email: testEmail.trim() || null,
  });

  const validate = () => {
    if (!subject.trim()) { toast.error("Escribe el asunto del correo"); return false; }
    if (!body.replace(/<[^>]*>/g, "").trim()) { toast.error("Escribe el cuerpo del mensaje"); return false; }
    return true;
  };

  const pickRaffle = (id) => {
    setRaffleId(id);
    if (id) setLink(`${window.location.origin}/r/${id}`);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/raffles/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(res.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo subir la imagen");
    } finally { setUploading(false); }
  };

  const sendTest = async () => {
    if (!validate()) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail.trim())) {
      toast.error("Escribe el correo donde quieres recibir la prueba");
      return;
    }
    setTesting(true);
    try {
      const res = await api.post("/campaigns/test", payload());
      toast.success(`Correo de prueba enviado a ${res.data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la prueba");
    } finally { setTesting(false); }
  };

  const send = async () => {
    if (!validate()) return;
    if (recipients.length === 0) { toast.error("Selecciona al menos un cliente"); return; }
    setSending(true);
    try {
      const res = await api.post("/campaigns", payload());
      toast.success(`Campaña en camino a ${res.data.recipients_total} cliente(s)`);
      onSent?.();
      onOpenChange(false);
      setSubject(""); setBody(""); setLink(""); setRaffleId(""); setImageUrl(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar la campaña");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="campaign-dialog" className="max-w-xl max-h-[92vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Crear campaña de correo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-neutral-500">Asunto</Label>
            <Input data-testid="campaign-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Ej: ¡Últimos números disponibles!" className="rounded-xl mt-1" maxLength={200} />
          </div>

          <div>
            <Label className="text-xs text-neutral-500">Cuerpo del mensaje</Label>
            <div className="mt-1">
              <RichTextEditor value={body} onChange={setBody} placeholder="Escribe tu mensaje. Puedes usar negrita, cursiva, listas y enlaces." />
            </div>
          </div>

          <div>
            <Label className="text-xs text-neutral-500">Rifa a promocionar (define el diseño del correo)</Label>
            <select data-testid="campaign-raffle-select" value={raffleId} onChange={(e) => pickRaffle(e.target.value)}
              className="w-full mt-1 h-10 rounded-xl border border-neutral-300 bg-white px-3 text-sm outline-none focus:border-[#00AEEF]">
              <option value="">Sin rifa (usa tu marca)</option>
              {raffles.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>

          <div>
            <Label className="text-xs text-neutral-500">Link de la rifa (botón “Ver la rifa”)</Label>
            <Input data-testid="campaign-link" value={link} onChange={(e) => setLink(e.target.value)}
              placeholder="https://mctecheventos.com/r/..." className="rounded-xl mt-1" />
          </div>

          <div>
            <Label className="text-xs text-neutral-500">Imagen promocional (opcional)</Label>
            {imageUrl ? (
              <div className="mt-1.5 relative inline-block">
                <img src={imageUrl} alt="Promoción" className="h-28 w-auto rounded-xl border border-neutral-200 object-cover" />
                <button data-testid="campaign-image-remove" onClick={() => setImageUrl(null)}
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white border border-neutral-200 shadow flex items-center justify-center text-neutral-500 hover:text-red-500">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <label data-testid="campaign-image-upload" className="mt-1.5 flex items-center gap-2 cursor-pointer rounded-xl border border-dashed border-neutral-300 px-3.5 py-3 text-sm text-neutral-500 hover:border-[#00AEEF] hover:text-[#0092CC] transition-colors">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                {uploading ? "Subiendo..." : "Subir imagen (JPG, PNG o WebP)"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            )}
          </div>

          <div>
            <Label className="text-xs text-neutral-500">Correo para la prueba</Label>
            <Input data-testid="campaign-test-email" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
              placeholder="tucorreo@ejemplo.com" className="rounded-xl mt-1" />
            <p className="text-[11px] text-neutral-400 mt-1">Recibirás el correo tal como lo verán tus clientes, con el asunto marcado como [PRUEBA].</p>
          </div>

          <div className="rounded-xl bg-[#E6F7FE] px-4 py-3 text-xs text-neutral-600">
            Destinatarios seleccionados: <b data-testid="campaign-recipients-count">{recipients.length}</b>. Se envían ~100 correos por minuto para evitar el filtro de spam.
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Button data-testid="campaign-test-btn" onClick={sendTest} disabled={testing || sending}
              variant="outline" className="rounded-xl gap-2 flex-1">
              {testing ? <Loader2 size={16} className="animate-spin" /> : <FlaskConical size={16} />}
              Enviar correo de prueba
            </Button>
            <Button data-testid="campaign-send-btn" onClick={send} disabled={sending || testing || recipients.length === 0}
              className="rounded-xl gap-2 flex-1 bg-[#00AEEF] hover:bg-[#0092CC] text-white">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Enviar a {recipients.length}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
